import React, {memo, useCallback, useMemo, useState} from "react"
import {useRPC} from "../../../../hooks/rpc"
import {useDispatch, useSelector} from "react-redux"
import {useLocalization} from "../../../../hooks/localization"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingCheckBoxComponent} from "../../../../components/Settings/SettingCheckboxComponent"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {SettingInputComponent} from "../../../../components/Settings/SettingInputComponent"
import {useKeyboard} from "../../../../hooks/keyboard"
import {useMessage} from "../../../../hooks/message"
import {useHistory} from "react-router-dom"
import {WorkerConfigModel} from "../../../../types/worker-config"
import {setWorkerConfig} from "../../../../store/control/actions"
import "./index.scss"

const BACK_URL = "/settings/advanced-settings"
export const WorkerConfigScreen = memo(function WorkerConfigScreen() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const {failure, success} = useMessage()
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard()
    const history = useHistory()
    const dispatch = useDispatch()
    const workerConfigList = useSelector(state => state.control.workerConfig)
    const [panelID, setPanelID] = useState<string>(workerConfigList?.panel?.id)
    const [panelToken, setPanelToken] = useState<string>(workerConfigList?.panel?.token)
    const [panelType, setPanelType] = useState<string>(workerConfigList?.panel?.type)
    const [panelRotation, setPanelRotation] = useState<string>(workerConfigList?.panel?.rotation)
    const [incognitoEnabled, setIncognitoEnabled] = useState<boolean>(workerConfigList?.chromium?.incognito_enabled)
    const [executable, setExecutable] = useState<string>(workerConfigList?.chromium?.executable)
    const [remoteDebugEnabled, setRemoteDebugEnabled] = useState<boolean>(
        workerConfigList?.chromium?.remote_debug_enabled
    )
    const [remoteDebugPort, setRemoteDebugPort] = useState<string>(
        String(workerConfigList?.chromium?.remote_debug_port)
    )
    const [intercomLevel, setIntercomLevel] = useState<string>(String(workerConfigList?.sound?.intercom_level))
    const [intercomCallFlat, setIntercomCallFlat] = useState<string>(
        String(workerConfigList?.sound?.incoming_call_flat_front_level)
    )
    const [intercomCallOutside, setIntercomCallOutside] = useState<string>(
        String(workerConfigList?.sound?.incoming_call_outside_panel_level)
    )
    const [alarmLevel, setAlarmLevel] = useState<string>(String(workerConfigList?.sound?.alarm_level))
    const [microphoneLevel, setMicrophoneLevel] = useState<string>(String(workerConfigList?.sound?.microphone_level))
    const [cardID, setCardID] = useState<string>(workerConfigList?.sound?.card_id)
    const [maxValue, setMaxValue] = useState<string>(String(workerConfigList?.sound?.max_value))
    const [minValue, setMinValue] = useState<string>(String(workerConfigList?.sound?.min_value))
    const [micCardID, setMicCardID] = useState<string>(workerConfigList?.sound?.mic_card_id)
    const [micMaxValue, setMicMaxValue] = useState<string>(String(workerConfigList?.sound?.mic_max_value))
    const [micMinValue, setMicMinValue] = useState<string>(String(workerConfigList?.sound?.mic_min_value))
    const [clickSound, setClickSound] = useState<boolean>(workerConfigList?.sound?.click_sound)
    const [sipEnabled, setSipEnabled] = useState<boolean>(workerConfigList?.intercom?.sip_enabled)
    const [bellPeriod, setBellPeriod] = useState<string>(String(workerConfigList?.intercom?.bell_period_in_seconds))
    const [hangupCallOpened, setHangupCallOpened] = useState<boolean>(
        workerConfigList?.intercom?.hangup_call_opened_door
    )
    const [panelSessionTimeout, setPanelSessionTimeout] = useState<string>(
        String(workerConfigList?.authentication?.panel_session_timeout_in_seconds)
    )
    const [scanAfterLaunch, setScanAfterLaunch] = useState<string>(
        String(workerConfigList?.control?.scan_after_launch_delay_for_initialize_milliseconds)
    )
    const [scanAdditional, setScanAdditional] = useState<string>(
        String(workerConfigList?.control?.scan_additional_write_after_delay_millisecond)
    )
    const [shouldRestartAfterIO, setShouldRestartAfterIO] = useState<boolean>(
        workerConfigList?.mapping?.should_restart_after_mapping_for_io
    )
    const [timeBackupConfig, setTimeBackupConfig] = useState<string>(
        String(workerConfigList?.mapping?.time_backup_config_in_miliseconds)
    )
    const [shouldRestartAfterGlass, setShouldRestartAfterGlass] = useState<boolean>(
        workerConfigList?.mapping?.should_restart_after_mapping_for_glass
    )
    const [beforeRestartDelay, setBeforeRestartDelay] = useState<string>(
        String(workerConfigList?.mapping?.before_restart_delay_in_seconds)
    )
    const [maxFPS, setMaxFPS] = useState<string>(String(workerConfigList?.rtsp?.max_fps))
    const [logFolder, setLogFolder] = useState<string>(workerConfigList?.log_folder)
    const [imageVersion, setImageVersion] = useState<string>(workerConfigList?.image_version)
    const [email, setEmail] = useState<string>(workerConfigList?.ifttt?.e_mail)
    const [password, setPassword] = useState<string>(workerConfigList?.ifttt?.password)
    const [server, setServer] = useState<string>(workerConfigList?.ifttt?.server)
    const [authUser, setAuthUser] = useState<string>(workerConfigList?.ifttt?.auth_user)
    const [authPass, setAuthPass] = useState<string>(workerConfigList?.ifttt?.auth_pass)
    const [token, setToken] = useState<string>(workerConfigList?.ifttt?.token)
    const [isEnabled, setIsEnabled] = useState<boolean>(workerConfigList?.ifttt?.is_enabled)
    const [goroutineCount, setGoroutineCount] = useState<string>(String(workerConfigList?.goroutine_count))
    const [securityCode, setSecurityCode] = useState<string>(workerConfigList?.security_code)
    const [changeIndicator, setChangeIndicator] = useState<string>(workerConfigList?.change_indicator)
    const [realTimeCheckSeconds, setRealTimeCheckSeconds] = useState<string>(
        String(workerConfigList?.energy_monitor?.real_time_check_seconds)
    )
    const [protocol, setProtocol] = useState<string>(workerConfigList?.sup_connection?.protocol)
    const [address, setAddress] = useState<string>(workerConfigList?.sup_connection?.address)
    const [port, setPort] = useState<string>(workerConfigList?.sup_connection?.port)
    const [executorType, setExecutorType] = useState<string>(workerConfigList?.executor_type)
    const [uartName, setUartName] = useState<string>(workerConfigList?.uart?.name)
    const [uartBaud, setUartBaud] = useState<string>(String(workerConfigList?.uart?.baud))
    const [uartReadTimeout, setUartReadTimeout] = useState<string>(
        String(workerConfigList?.uart?.read_timeout_millisecond)
    )
    const [uartWriteAfterDelay, setUartWriteAfterDelay] = useState<string>(
        String(workerConfigList?.uart?.write_after_delay_millisecond)
    )
    const [uartPacketControl, setUartPacketControl] = useState<string>(
        String(workerConfigList?.uart?.packet_control_period_millisecond)
    )
    const [uartAskVersionDelay, setUartAskVersionDelay] = useState<string>(
        String(workerConfigList?.uart?.ask_version_delay_in_millisecond)
    )
    const [uartVersionTimeout, setUartVersionTimeout] = useState<string>(
        String(workerConfigList?.uart?.version_timeout_in_millisecond)
    )
    const [uartIgnoreMulticast, setUartIgnoreMulticast] = useState<boolean>(workerConfigList?.uart?.ignore_multicast)
    const [uartOkRetryCount, setUartOkRetryCount] = useState<string>(
        String(workerConfigList?.uart?.no_ok_config?.retry_count)
    )
    const [uartOkTimeoutMillisecond, setUartOkTimeoutMillisecond] = useState<string>(
        String(workerConfigList?.uart?.no_ok_config?.timeout_millisecond)
    )
    const [uartResponseRetryCount, setUartResponseRetryCount] = useState<string>(
        String(workerConfigList?.uart?.no_response_config?.retry_count)
    )
    const [uartResponseTimeoutMillisecond, setUartResponseTimeoutMillisecond] = useState<string>(
        String(workerConfigList?.uart?.no_response_config?.timeout_millisecond)
    )
    const [uartSendErrorRetry, setUartSendErrorRetry] = useState<string>(
        String(workerConfigList?.uart?.send_error_retry_count)
    )
    const [uartButton, setUartButton] = useState<string>(workerConfigList?.uart?.commands?.button)
    const [uartError, setUartError] = useState<string>(workerConfigList?.uart?.commands?.uart_error)
    const [uartReset, setUartReset] = useState<string>(workerConfigList?.uart?.commands?.uart_reset)
    const [uartOK, setUartOK] = useState<string>(workerConfigList?.uart?.commands?.ok)
    const [uartSendError, setUartSendError] = useState<string>(workerConfigList?.uart?.commands?.send_error)
    const [uartACK, setUartACK] = useState<string>(workerConfigList?.uart?.commands?.ack)
    const [uartReceive, setUartReceive] = useState<string>(workerConfigList?.uart?.commands?.receive)
    const [uartReceiveLocal, setUartReceiveLocal] = useState<string>(workerConfigList?.uart?.commands?.receive_local)
    const [uartBell, setUartBell] = useState<string>(workerConfigList?.uart?.commands?.bell)
    const [uartGetRTC, setUartGetRTC] = useState<string>(workerConfigList?.uart?.commands?.get_rtc)
    const [uartSomFactoryReset, setUartSomFactoryReset] = useState<string>(
        workerConfigList?.uart?.commands?.som_factory_reset
    )
    const [uartVoltageSupply, setUartVoltageSupply] = useState<string>(workerConfigList?.uart?.commands?.voltage_supply)
    const [uartMessagePrefix, setUartMessagePrefix] = useState<string>(workerConfigList?.uart?.message_prefix)
    const [uartScanDeviceCount, setUartScanDeviceCount] = useState<string>(
        String(workerConfigList?.uart?.scan_device_count)
    )
    const [uartScanDeviceDelayMS, setUartScanDeviceDelayMS] = useState<string>(
        String(workerConfigList?.uart?.scan_device_delay_millisecond)
    )
    const [uartMessagePrefixForBootLog, setUartMessagePrefixForBootLog] = useState<string>(
        workerConfigList?.uart?.message_prefix_for_boot_log
    )
    const [uartInitTemp, setUartInitTemp] = useState<string>(workerConfigList?.uart?.commands?.init_temp)
    const [uartActiveAddr, setUartActiveAddr] = useState<string>(workerConfigList?.uart?.commands?.active_addr)
    const [uartSavedAddr, setUartSavedAddr] = useState<string>(workerConfigList?.uart?.commands?.saved_addr)
    const [uartVoltageSupply3V, setUartVoltageSupply3V] = useState<string>(
        workerConfigList?.uart?.commands?.voltage_supply_3v
    )
    const [uartIOTemp, setUartIOTemp] = useState<string>(workerConfigList?.uart?.commands?.io_temperature)
    const [uartTempSensorValue, setUartTempSensorValue] = useState<string>(
        workerConfigList?.uart?.commands?.temperature_sensor_value
    )
    const [screenSaverType, setScreenSaverType] = useState<string>(workerConfigList?.screen_saver?.screen_saver_type)
    const [screenSaverTiming, setScreenSaverTiming] = useState<string>(
        String(workerConfigList?.screen_saver?.screen_saver_timing_minute)
    )
    const [screenSaverActive, setScreenSaverActive] = useState<string>(
        String(workerConfigList?.screen_saver?.screen_saver_active_brightness)
    )
    const [screenStandardBrightness, setScreenStandardBrightness] = useState<string>(
        String(workerConfigList?.screen_saver?.screen_standard_brightness)
    )
    const [screenSaverStatus, setScreenSaverStatus] = useState<boolean>(
        workerConfigList?.screen_saver?.screen_saver_status
    )
    const [screenSleepSecond, setScreenSleepSecond] = useState<string>(
        String(workerConfigList?.screen_saver?.screen_sleep_second)
    )
    const [screenLockStatus, setScreenLockStatus] = useState<boolean>(workerConfigList?.screen_lock?.screen_lock_status)
    const [screenLockTimingMinute, setScreenLockTimingMinute] = useState<string>(
        String(workerConfigList?.screen_lock?.screen_lock_timing_minute)
    )
    const [sendNotification, setSendNotification] = useState<boolean>(workerConfigList?.screen_lock?.send_notification)
    const [debugMode, setDebugMode] = useState<boolean>(workerConfigList?.uart?.debug_mode)
    const [showHomepageItem, setShowHomepageItem] = useState<boolean>(workerConfigList?.show_homepage_items_by_gateway)
    const [temperatureLog, setTemperatureLog] = useState<boolean>(workerConfigList?.temperature_log_is_active)
    const [temperatureReadingPeriod, setTemperatureReadingPeriod] = useState<string>(
        String(workerConfigList?.temperature_reading_period)
    )
    const [servicePort, setServicePort] = useState<string>(workerConfigList?.rtsp?.service_port)
    const [serviceKey, setServiceKey] = useState<string>(workerConfigList?.rtsp?.service_key)
    const [encryptionKey, setEncryptionKey] = useState<string>(workerConfigList?.rtsp?.encryption_key)
    const [state1, setState1] = useState<string>(workerConfigList?.uart?.pinout?.state1)
    const [state2, setState2] = useState<string>(workerConfigList?.uart?.pinout?.state2)

    const onBackPressed = useCallback(() => history.push(BACK_URL), [history])
    const onSavePressed = useCallback(() => {
        const workerConfigModel: WorkerConfigModel = {
            show_homepage_items_by_gateway: showHomepageItem,
            temperature_reading_period: Number(temperatureReadingPeriod),
            temperature_log_is_active: temperatureLog,
            authentication: {
                panel_session_timeout_in_seconds: Number(panelSessionTimeout)
            },
            change_indicator: changeIndicator,
            chromium: {
                executable: executable,
                incognito_enabled: incognitoEnabled,
                remote_debug_enabled: remoteDebugEnabled,
                remote_debug_port: Number(remoteDebugPort)
            },
            control: {
                scan_after_launch_delay_for_initialize_milliseconds: Number(scanAfterLaunch),
                scan_additional_write_after_delay_millisecond: Number(scanAdditional)
            },
            energy_monitor: {
                real_time_check_seconds: Number(realTimeCheckSeconds)
            },
            executor_type: executorType,
            goroutine_count: Number(goroutineCount),
            ifttt: {
                auth_user: authUser,
                auth_pass: authPass,
                token: token,
                is_enabled: isEnabled,
                e_mail: email,
                password: password,
                server: server
            },
            image_version: imageVersion,
            intercom: {
                sip_enabled: sipEnabled,
                bell_period_in_seconds: Number(bellPeriod),
                hangup_call_opened_door: hangupCallOpened
            },
            log_folder: logFolder,
            mapping: {
                should_restart_after_mapping_for_io: shouldRestartAfterIO,
                should_restart_after_mapping_for_glass: shouldRestartAfterGlass,
                before_restart_delay_in_seconds: Number(beforeRestartDelay),
                time_backup_config_in_miliseconds: Number(timeBackupConfig)
            },
            panel: {
                id: panelID,
                token: panelToken,
                type: panelType,
                rotation: panelRotation
            },
            rtsp: {
                max_fps: Number(maxFPS),
                service_port: servicePort,
                encryption_key: encryptionKey,
                service_key: serviceKey
            },
            screen_lock: {
                screen_lock_status: screenLockStatus,
                screen_lock_timing_minute: Number(screenLockTimingMinute),
                send_notification: sendNotification
            },
            screen_saver: {
                screen_saver_type: screenSaverType,
                screen_saver_timing_minute: Number(screenSaverTiming),
                screen_saver_active_brightness: Number(screenSaverActive),
                screen_standard_brightness: Number(screenStandardBrightness),
                screen_saver_status: screenSaverStatus,
                screen_sleep_second: Number(screenSleepSecond)
            },
            security_code: securityCode,
            sound: {
                intercom_level: Number(intercomLevel),
                incoming_call_flat_front_level: Number(intercomCallFlat),
                incoming_call_outside_panel_level: Number(intercomCallOutside),
                alarm_level: Number(alarmLevel),
                microphone_level: Number(microphoneLevel),
                card_id: cardID,
                max_value: Number(maxValue),
                min_value: Number(minValue),
                mic_card_id: micCardID,
                mic_max_value: Number(micMaxValue),
                mic_min_value: Number(micMinValue),
                click_sound: clickSound
            },
            sup_connection: {
                protocol: protocol,
                address: address,
                port: port
            },
            uart: {
                ask_version_delay_in_millisecond: Number(uartAskVersionDelay),
                baud: Number(uartBaud),
                commands: {
                    button: uartButton,
                    uart_error: uartError,
                    uart_reset: uartReset,
                    ok: uartOK,
                    send_error: uartSendError,
                    ack: uartACK,
                    receive: uartReceive,
                    receive_local: uartReceiveLocal,
                    bell: uartBell,
                    get_rtc: uartGetRTC,
                    som_factory_reset: uartSomFactoryReset,
                    voltage_supply: uartVoltageSupply,
                    init_temp: uartInitTemp,
                    active_addr: uartActiveAddr,
                    saved_addr: uartSavedAddr,
                    voltage_supply_3v: uartVoltageSupply3V,
                    io_temperature: uartIOTemp,
                    temperature_sensor_value: uartTempSensorValue
                },
                message_prefix_for_boot_log: uartMessagePrefixForBootLog,
                ignore_multicast: uartIgnoreMulticast,
                message_prefix: uartMessagePrefix,
                debug_mode: debugMode,
                name: uartName,
                no_ok_config: {
                    retry_count: Number(uartOkRetryCount),
                    timeout_millisecond: Number(uartOkTimeoutMillisecond)
                },
                no_response_config: {
                    retry_count: Number(uartResponseRetryCount),
                    timeout_millisecond: Number(uartResponseTimeoutMillisecond)
                },
                scan_device_count: Number(uartScanDeviceCount),
                scan_device_delay_millisecond: Number(uartScanDeviceDelayMS),
                send_error_retry_count: Number(uartSendErrorRetry),
                version_timeout_in_millisecond: Number(uartVersionTimeout),
                read_timeout_millisecond: Number(uartReadTimeout),
                write_after_delay_millisecond: Number(uartWriteAfterDelay),
                packet_control_period_millisecond: Number(uartPacketControl),
                pinout: {
                    state1: state1,
                    state2: state2
                }
            }
        }
        return rpc
            .updateWorkerConfig(workerConfigModel)
            .then(() => {
                dispatch(setWorkerConfig(workerConfigModel))
                success(t("successfully_edited"))
                onBackPressed()
            })
            .catch(failure)
    }, [
        onBackPressed,
        failure,
        success,
        t,
        dispatch,
        rpc,
        panelID,
        panelToken,
        panelType,
        panelRotation,
        incognitoEnabled,
        executable,
        remoteDebugEnabled,
        remoteDebugPort,
        intercomLevel,
        intercomCallFlat,
        intercomCallOutside,
        alarmLevel,
        microphoneLevel,
        cardID,
        maxValue,
        minValue,
        micCardID,
        micMaxValue,
        micMinValue,
        clickSound,
        sipEnabled,
        bellPeriod,
        hangupCallOpened,
        panelSessionTimeout,
        scanAfterLaunch,
        scanAdditional,
        shouldRestartAfterIO,
        shouldRestartAfterGlass,
        beforeRestartDelay,
        maxFPS,
        logFolder,
        imageVersion,
        email,
        password,
        server,
        authUser,
        authPass,
        token,
        isEnabled,
        goroutineCount,
        securityCode,
        changeIndicator,
        realTimeCheckSeconds,
        protocol,
        address,
        port,
        executorType,
        uartName,
        uartBaud,
        uartReadTimeout,
        uartWriteAfterDelay,
        uartPacketControl,
        uartAskVersionDelay,
        uartVersionTimeout,
        uartIgnoreMulticast,
        uartOkRetryCount,
        uartOkTimeoutMillisecond,
        uartResponseRetryCount,
        uartResponseTimeoutMillisecond,
        uartSendErrorRetry,
        uartButton,
        uartError,
        uartReset,
        uartOK,
        uartSendError,
        uartACK,
        uartReceive,
        uartReceiveLocal,
        uartBell,
        uartGetRTC,
        uartSomFactoryReset,
        uartVoltageSupply,
        uartMessagePrefix,
        uartScanDeviceCount,
        uartScanDeviceDelayMS,
        screenSaverType,
        screenSaverTiming,
        screenSaverActive,
        screenStandardBrightness,
        screenSaverStatus,
        screenSleepSecond,
        screenLockStatus,
        screenLockTimingMinute,
        sendNotification,
        timeBackupConfig,
        uartActiveAddr,
        uartIOTemp,
        uartInitTemp,
        uartMessagePrefixForBootLog,
        uartSavedAddr,
        uartTempSensorValue,
        uartVoltageSupply3V,
        debugMode,
        encryptionKey,
        serviceKey,
        servicePort,
        showHomepageItem,
        temperatureLog,
        temperatureReadingPeriod,
        state1,
        state2
    ])

    const buttons = useMemo(
        () => [
            {
                text: t("save"),
                className: "green",
                onClick: onSavePressed
            }
        ],
        [t, onSavePressed]
    )

    const onBlur = useCallback(() => {
        setActiveInput(null)
    }, [setActiveInput])
    return (
        <PasswordContainer requiredRole="developer" isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className="service-edit"
                backUrl={BACK_URL}
                currentInput={currentInput}
            >
                <div className="content-divider">
                    <div className="content-wrapper">
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Panel</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"ID"}
                                value={panelID}
                                setValue={setPanelID}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Token"}
                                value={panelToken}
                                setValue={setPanelToken}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Type"}
                                value={panelType}
                                setValue={setPanelType}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Rotation"}
                                value={panelRotation + "°"}
                                setValue={setPanelRotation}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Chromium</div>
                            <SettingCheckBoxComponent
                                label={"Incognito Enabled"}
                                set={() => setIncognitoEnabled(p => !p)}
                                value={incognitoEnabled}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Executable"}
                                value={executable}
                                setValue={setExecutable}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Remote Debug Enabled"}
                                set={() => setRemoteDebugEnabled(p => !p)}
                                value={remoteDebugEnabled}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Remote Debug Port"}
                                value={remoteDebugPort}
                                setValue={setRemoteDebugPort}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Sound</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Intercom Level"}
                                value={intercomLevel}
                                setValue={setIntercomLevel}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Incoming Call Flat Front Level"}
                                value={intercomCallFlat}
                                setValue={setIntercomCallFlat}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Incoming Call Outside Panel Level"}
                                value={intercomCallOutside}
                                setValue={setIntercomCallOutside}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Alarm Level"}
                                value={alarmLevel}
                                setValue={setAlarmLevel}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Microphone Level"}
                                value={microphoneLevel}
                                setValue={setMicrophoneLevel}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Card ID"}
                                value={cardID}
                                setValue={setCardID}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Max Value"}
                                value={maxValue}
                                setValue={setMaxValue}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Min Value"}
                                value={minValue}
                                setValue={setMinValue}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Mic Card ID"}
                                value={micCardID}
                                setValue={setMicCardID}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Mic Max Value"}
                                value={micMaxValue}
                                setValue={setMicMaxValue}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Mic Min Value"}
                                value={micMinValue}
                                setValue={setMicMinValue}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Click Sound"}
                                set={() => setClickSound(p => !p)}
                                value={clickSound}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Intercom</div>
                            <SettingCheckBoxComponent
                                label={"Sip Enabled"}
                                set={() => setSipEnabled(p => !p)}
                                value={sipEnabled}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Bell Period in Seconds"}
                                value={bellPeriod}
                                setValue={setBellPeriod}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Hangup Call Opened Door"}
                                set={() => setHangupCallOpened(p => !p)}
                                value={hangupCallOpened}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Authentication</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Panel Session Timeout in Seconds"}
                                value={panelSessionTimeout}
                                setValue={setPanelSessionTimeout}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Control</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Scan After Launch Delay For Initialize Milliseconds"}
                                value={scanAfterLaunch}
                                setValue={setScanAfterLaunch}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Scan Additional Write AfterD elay Millisecond"}
                                value={scanAdditional}
                                setValue={setScanAdditional}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Mapping</div>
                            <SettingCheckBoxComponent
                                label={"Should Restart After Mapping For IO"}
                                set={() => setShouldRestartAfterIO(p => !p)}
                                value={shouldRestartAfterIO}
                            />
                            <SettingCheckBoxComponent
                                label={"Should Restart After Mapping For Glass"}
                                set={() => setShouldRestartAfterGlass(p => !p)}
                                value={shouldRestartAfterGlass}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Before Restart Delay in Seconds"}
                                value={beforeRestartDelay}
                                setValue={setBeforeRestartDelay}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Time Backup Config in Milliseconds"}
                                value={timeBackupConfig}
                                setValue={setTimeBackupConfig}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">RTSP</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Max FPS"}
                                value={maxFPS}
                                setValue={setMaxFPS}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />

                            <SettingInputComponent
                                placeholder={""}
                                label={"Service Port"}
                                value={servicePort}
                                setValue={setServicePort}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Service Key"}
                                value={serviceKey}
                                setValue={setServiceKey}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Encryption Key"}
                                value={encryptionKey}
                                setValue={setEncryptionKey}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <SettingInputComponent
                            placeholder={""}
                            label={"Log Folder"}
                            value={logFolder}
                            setValue={setLogFolder}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Image Version"}
                            value={imageVersion}
                            setValue={setImageVersion}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <div className="worker-config-set-row inside-margin">
                            <div className="worker-config-title">IFTTT</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Email"}
                                value={email}
                                setValue={setEmail}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Password"}
                                value={password}
                                setValue={setPassword}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Server"}
                                value={server}
                                setValue={setServer}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Auth User"}
                                value={authUser}
                                setValue={setAuthUser}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Auth Pass"}
                                value={authPass}
                                setValue={setAuthPass}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Auth Token"}
                                value={token}
                                setValue={setToken}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Is Enabled"}
                                set={() => setIsEnabled(p => !p)}
                                value={isEnabled}
                            />
                        </div>
                        <SettingCheckBoxComponent
                            label={"Show Homepage Items by Gateway"}
                            set={() => setShowHomepageItem(p => !p)}
                            value={showHomepageItem}
                        />
                        <SettingCheckBoxComponent
                            label={"Temperature Log"}
                            set={() => setTemperatureLog(p => !p)}
                            value={temperatureLog}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Temperature Reading Period"}
                            value={temperatureReadingPeriod}
                            setValue={setTemperatureReadingPeriod}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Goroutine Count"}
                            value={goroutineCount}
                            setValue={setGoroutineCount}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Security Code"}
                            value={securityCode}
                            setValue={setSecurityCode}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <SettingInputComponent
                            placeholder={""}
                            label={"Change Indicator"}
                            value={changeIndicator}
                            setValue={setChangeIndicator}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}
                        />
                        <div className="worker-config-set-row inside-margin">
                            <div className="worker-config-title">Energy Monitor</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Real Time Check Seconds"}
                                value={realTimeCheckSeconds}
                                setValue={setRealTimeCheckSeconds}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                    </div>
                    <div className="content-wrapper">
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Sup Connection</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Protocol"}
                                value={protocol}
                                setValue={setProtocol}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Address"}
                                value={address}
                                setValue={setAddress}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Port"}
                                value={port}
                                setValue={setPort}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Executor Type</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Executor Type"}
                                value={executorType}
                                setValue={setExecutorType}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">UART</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Name"}
                                value={uartName}
                                setValue={setUartName}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Baud"}
                                value={uartBaud}
                                setValue={setUartBaud}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Read Timeout Millisecond"}
                                value={uartReadTimeout}
                                setValue={setUartReadTimeout}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Write After Delay Millisecond"}
                                value={uartWriteAfterDelay}
                                setValue={setUartWriteAfterDelay}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />

                            <SettingInputComponent
                                placeholder={""}
                                label={"Packet Control Period Millisecond"}
                                value={uartPacketControl}
                                setValue={setUartPacketControl}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />

                            <SettingInputComponent
                                placeholder={""}
                                label={"Ask Version Delay in Millisecond"}
                                value={uartAskVersionDelay}
                                setValue={setUartAskVersionDelay}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />

                            <SettingInputComponent
                                placeholder={""}
                                label={"Version Timeout in Millisecond"}
                                value={uartVersionTimeout}
                                setValue={setUartVersionTimeout}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Ignore Multicast"}
                                set={() => setUartIgnoreMulticast(p => !p)}
                                value={uartIgnoreMulticast}
                            />
                            <SettingCheckBoxComponent
                                label={"Debug Mode"}
                                set={() => setDebugMode(p => !p)}
                                value={debugMode}
                            />
                            <div className="worker-config-set-row inside-margin">
                                <div className="worker-config-title">No Ok Config</div>
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Retry Count"}
                                    value={uartOkRetryCount}
                                    setValue={setUartOkRetryCount}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Timeout Millisecond"}
                                    value={uartOkTimeoutMillisecond}
                                    setValue={setUartOkTimeoutMillisecond}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            </div>
                            <div className="worker-config-set-row inside-margin">
                                <div className="worker-config-title">No Response Config</div>
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Retry Count"}
                                    value={uartResponseRetryCount}
                                    setValue={setUartResponseRetryCount}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Timeout Millisecond"}
                                    value={uartResponseTimeoutMillisecond}
                                    setValue={setUartResponseTimeoutMillisecond}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            </div>
                            <div className="worker-config-set-row inside-margin">
                                <div className="worker-config-title">Pin Out</div>
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"State 1"}
                                    value={state1}
                                    setValue={setState1}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"State 2"}
                                    value={state2}
                                    setValue={setState2}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            </div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Send Error Retry Count"}
                                value={uartSendErrorRetry}
                                setValue={setUartSendErrorRetry}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <div className="worker-config-set-row inside-margin">
                                <div className="worker-config-title">Commands</div>
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Button"}
                                    value={uartButton}
                                    setValue={setUartButton}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Uart Error"}
                                    value={uartError}
                                    setValue={setUartError}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Uart Reset"}
                                    value={uartReset}
                                    setValue={setUartReset}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"OK"}
                                    value={uartOK}
                                    setValue={setUartOK}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Send Error"}
                                    value={uartSendError}
                                    setValue={setUartSendError}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"ACK"}
                                    value={uartACK}
                                    setValue={setUartACK}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Receive"}
                                    value={uartReceive}
                                    setValue={setUartReceive}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Receive Local"}
                                    value={uartReceiveLocal}
                                    setValue={setUartReceiveLocal}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Bell"}
                                    value={uartBell}
                                    setValue={setUartBell}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Get RTC"}
                                    value={uartGetRTC}
                                    setValue={setUartGetRTC}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Som Factory Reset"}
                                    value={uartSomFactoryReset}
                                    setValue={setUartSomFactoryReset}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Voltage Supply"}
                                    value={uartVoltageSupply}
                                    setValue={setUartVoltageSupply}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Init Temp"}
                                    value={uartInitTemp}
                                    setValue={setUartInitTemp}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Active ADDR"}
                                    value={uartActiveAddr}
                                    setValue={setUartActiveAddr}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Saved ADDR"}
                                    value={uartSavedAddr}
                                    setValue={setUartSavedAddr}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />{" "}
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Voltage Supply 3V"}
                                    value={uartVoltageSupply3V}
                                    setValue={setUartVoltageSupply3V}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"IO Temperature"}
                                    value={uartIOTemp}
                                    setValue={setUartIOTemp}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                                <SettingInputComponent
                                    placeholder={""}
                                    label={"Temperature Sensor Value"}
                                    value={uartTempSensorValue}
                                    setValue={setUartTempSensorValue}
                                    setActiveInput={setActiveInput}
                                    onBlur={onBlur}
                                />
                            </div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Message Prefix Boot Log"}
                                value={uartMessagePrefixForBootLog}
                                setValue={setUartMessagePrefixForBootLog}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Message Prefix"}
                                value={uartMessagePrefix}
                                setValue={setUartMessagePrefix}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Scan Device Count"}
                                value={uartScanDeviceCount}
                                setValue={setUartScanDeviceCount}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Scan Device Delay Millisecond"}
                                value={uartScanDeviceDelayMS}
                                setValue={setUartScanDeviceDelayMS}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Screen Saver</div>
                            <SettingInputComponent
                                placeholder={""}
                                label={"Screen Saver Type"}
                                value={screenSaverType}
                                setValue={setScreenSaverType}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Screen Saver Timing Minute"}
                                value={screenSaverTiming}
                                setValue={setScreenSaverTiming}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Screen Saver Active Brightness"}
                                value={screenSaverActive}
                                setValue={setScreenSaverActive}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Screen Standard Brightness"}
                                value={screenStandardBrightness}
                                setValue={setScreenStandardBrightness}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Screen Saver Status"}
                                set={() => setScreenSaverStatus(p => !p)}
                                value={screenSaverStatus}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Screen Sleep Second"}
                                value={screenSleepSecond}
                                setValue={setScreenSleepSecond}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                        </div>
                        <div className="worker-config-set-row">
                            <div className="worker-config-title">Screen Lock</div>
                            <SettingCheckBoxComponent
                                label={"Screen Lock Status"}
                                set={() => setScreenLockStatus(p => !p)}
                                value={screenLockStatus}
                            />
                            <SettingInputComponent
                                placeholder={""}
                                label={"Screen Lock Timing Minute"}
                                value={screenLockTimingMinute}
                                setValue={setScreenLockTimingMinute}
                                setActiveInput={setActiveInput}
                                onBlur={onBlur}
                            />
                            <SettingCheckBoxComponent
                                label={"Send Notification"}
                                set={() => setSendNotification(p => !p)}
                                value={sendNotification}
                            />
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
            {renderKeyboard()}
        </PasswordContainer>
    )
})
